import { useEffect, useState } from 'react'
import { Check, Copy, Plus } from 'lucide-react'
import { Button, CopyField, Drawer, Notice, copyText } from '@/components/play'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import { workspaceError } from '@/lib/workspace'
import type { StreamResource } from '@/types/events'

interface SrtAddressesPanelProps {
  eventName: string
  cameras: StreamResource[]
  programme?: StreamResource
  onEnableLiveShow: () => Promise<StreamResource>
  liveShowBusy?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

type CopyState = 'idle' | 'copied' | 'failed'

/** Every SRT address in the event on one panel, so a host can pull the cameras and the live show into other systems. */
export function SrtAddressesPanel({
  eventName,
  cameras,
  programme,
  onEnableLiveShow,
  liveShowBusy = false,
  open,
  onOpenChange,
}: SrtAddressesPanelProps) {
  const [copyAll, setCopyAll] = useState<CopyState>('idle')
  const [enableError, setEnableError] = useState('')
  const outputs = cameras.flatMap((camera) => {
    const url = camera.attributes.connection_details?.external_egress_url
    return url ? [{ id: camera.id, name: camera.attributes.name, url }] : []
  })
  const showInput = programme?.attributes.connection_details?.external_ingress_url
  const showOutput = programme?.attributes.connection_details?.external_egress_url
  const showRtmpServer = programme?.attributes.connection_details?.rtmp_server_url

  useEffect(() => {
    if (copyAll !== 'copied') return
    const timer = window.setTimeout(() => setCopyAll('idle'), 2000)
    return () => window.clearTimeout(timer)
  }, [copyAll])

  async function copyAllOutputs() {
    const copied = await copyText(outputs.map((output) => `${output.name}: ${output.url}`).join('\n'))
    setCopyAll(copied ? 'copied' : 'failed')
  }

  async function enableLiveShow() {
    if (liveShowBusy) return
    setEnableError('')
    try {
      await onEnableLiveShow()
    } catch (failure) {
      setEnableError(workspaceError(failure, 'Your live show couldn’t be added. Try again.'))
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={eventName}
      title="SRT addresses"
      description="Pull your cameras and live show into any SRT receiver."
      className="pw-panel ph-no-capture ph-no-record"
    >
      <section className="pw-srt-section" aria-labelledby="srt-live-show-heading">
        <h3 id="srt-live-show-heading">Live show</h3>
        {programme ? (
          showInput || showOutput ? (
            <>
              {showInput && (
                <CopyField
                  label="Input"
                  copyLabel="Copy live show input"
                  value={showInput}
                  labelAccessory={<WorkspaceInfo label="About the live show input">Send your finished mix here from OBS or any SRT encoder.</WorkspaceInfo>}
                />
              )}
              {showRtmpServer && programme && (
                <>
                  <CopyField
                    label="RTMP server"
                    copyLabel="Copy live show RTMP server"
                    value={showRtmpServer}
                    labelAccessory={<WorkspaceInfo label="About the RTMP input">For a studio that only sends RTMP: this server with the stream key below.</WorkspaceInfo>}
                  />
                  <CopyField label="RTMP stream key" copyLabel="Copy live show stream key" value={programme.attributes.stream_key} />
                </>
              )}
              {showOutput && (
                <CopyField
                  label="Output"
                  copyLabel="Copy live show output"
                  value={showOutput}
                  labelAccessory={<WorkspaceInfo label="About the live show output">Pull the finished show into another system.</WorkspaceInfo>}
                />
              )}
            </>
          ) : (
            <p>The live show addresses are unavailable. Refresh the event to try again.</p>
          )
        ) : (
          <>
            <p>Send your finished OBS mix back to this event.</p>
            <Button variant="secondary" loading={liveShowBusy} onClick={() => void enableLiveShow()}>
              <Plus />
              {liveShowBusy ? 'Adding live show…' : 'Add live show'}
            </Button>
            {enableError && <Notice tone="error" alert>{enableError}</Notice>}
          </>
        )}
      </section>

      <section className="pw-srt-section" aria-labelledby="srt-cameras-heading">
        <div className="pw-srt-heading">
          <h3 id="srt-cameras-heading">
            Cameras<span>{cameras.length}</span>
          </h3>
          {outputs.length > 1 && (
            <Button variant="ghost" size="compact" onClick={() => void copyAllOutputs()}>
              {copyAll === 'copied' ? <Check /> : <Copy />}
              {copyAll === 'copied' ? 'Copied' : 'Copy all'}
            </Button>
          )}
        </div>
        {copyAll === 'failed' && (
          <Notice tone="error" alert>Copy didn’t work. Copy each address instead.</Notice>
        )}
        {cameras.length === 0 ? (
          <p>Add a camera to see its address here.</p>
        ) : (
          cameras.map((camera) => {
            const url = camera.attributes.connection_details?.external_egress_url
            return url ? (
              <CopyField key={camera.id} label={camera.attributes.name} copyLabel={`Copy ${camera.attributes.name} output`} value={url} />
            ) : (
              <p key={camera.id}>{camera.attributes.name}: address unavailable. Refresh the event to try again.</p>
            )
          })
        )}
        <span className="sr-only" role="status">{copyAll === 'copied' ? 'Copied all camera addresses' : ''}</span>
      </section>

      <p className="pw-srt-note">
        Outputs carry video while their source is live. Keep these addresses private: anyone with one can send or pull video.
      </p>
    </Drawer>
  )
}
